import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, take, takeUntil } from 'rxjs';
import { CanDeactivateType } from '../../../../../guards/form.guard';
import {
  NewsBannerContentType,
  NewsBannerCreateModel,
  NewsBannerModel,
  NewsBannerSlideModel,
  NewsBannerUpdateModel,
} from '../../../../../models/news-banner.model';
import { TrainingCategoryModel } from '../../../../../models/training-category.model';
import { AlertService } from '../../../../../services/alert.service';
import { FormService } from '../../../../../services/form.service';
import { NewsBannerService } from '../../../../../services/news-banner.service';
import { TrainingCategoryService } from '../../../../../services/training-category.service';
import { base64ToFile } from '../../../../../utils/file.utils';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
} from '../../../../../utils/form.utils';
import { ConfirmDialogComponent } from '../../../../shared-components/confirm-dialog/confirm-dialog.component';
import { ImageCropperDialogComponent } from '../../../../shared-components/image-cropper-dialog/image-cropper-dialog.component';

type ContentTypeOption = {
  value: NewsBannerContentType;
  label: string;
};

@Component({
  selector: 'app-news-banner-formular',
  templateUrl: './news-banner-formular.component.html',
  styleUrl: './news-banner-formular.component.scss',
  standalone: false,
})
export class NewsBannerFormularComponent implements OnInit, OnDestroy {
  newsBannerForm: FormGroup = new FormGroup({
    title: new FormControl(null, [
      Validators.required,
      Validators.maxLength(50),
    ]),
    subtitle: new FormControl(null, [Validators.maxLength(100)]),
    description: new FormControl(null, [
      Validators.required,
      Validators.maxLength(1200),
    ]),
    teaserText: new FormControl(null, [
      Validators.required,
      Validators.maxLength(100),
    ]),
    contentType: new FormControl(NewsBannerContentType.Image, [
      Validators.required,
    ]),
    image: new FormControl(null, [Validators.required]),
    categoryId: new FormControl(null),
    link: new FormControl(null),
    linkText: new FormControl(null, [Validators.maxLength(25)]),
  });
  initialFormValues = this.newsBannerForm.value;

  placeholderSlide: NewsBannerSlideModel = {
    title: 'Wähle ein Bild oder eine Kategorie aus',
    description:
      'Verwende das untere rechte Feld, um ein Bild oder eine Kategorie auszuwählen.',
    teaserText:
      'Verwende das untere rechte Feld, um ein Bild oder eine Kategorie auszuwählen.',
    color0: '#0E332E',
    color70: '#E6E6EB',
    color80: '#F0F0F3',
  };

  defaultCategory: TrainingCategoryModel = {
    id: 0,
    name: '',
    descriptionBold: '',
    description: '',
    color50: '',
    color0: '#1C665B',
    color70: '#bbd1ce',
    color80: '#D2E0DE',
    color90: '',
    isDisabled: false,
  };

  editMode: boolean = false;
  isLoading: boolean = false;
  selectedImage: string | null = null;
  selectedCategory: TrainingCategoryModel | null = null;
  newsBannerId: number | null = null;

  // imports from form.utils
  fieldRequired = fieldRequired;
  getTextAreaLength = getTextAreaLength;
  getMaxLengthFromValidator = getMaxLengthFromValidator;
  contentTypeOptions: ContentTypeOption[] = [
    { value: NewsBannerContentType.Image, label: 'Bild' },
    { value: NewsBannerContentType.Default, label: 'Standard' },
    { value: NewsBannerContentType.Category, label: 'Kategorie' },
  ];
  categoryOptions: { value: number; label: string }[] = [];
  availableCategories: TrainingCategoryModel[] = [];

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  $destroy: Subject<void> = new Subject<void>();

  constructor(
    private newsBannerService: NewsBannerService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private formService: FormService,
    private dialog: DialogService,
    private courseTypeService: TrainingCategoryService,
    private alertService: AlertService
  ) {}

  async ngOnInit(): Promise<void> {
    this.initializeNewsBannerId();
    this.handleContentTypeChange();
    this.handleCategoryChange();

    if (this.newsBannerId) {
      this.editMode = true;
      await this.fetchNewsBanner(this.newsBannerId);
    }

    await this.fetchCategories();
    this.isLoading = false;
  }

  private initializeNewsBannerId() {
    const idParam = this.activatedRoute.snapshot.params['id'];
    if (idParam) {
      const decodedComponent = decodeURIComponent(idParam);
      const decodedBase64 = atob(decodedComponent);
      this.newsBannerId = parseInt(decodedBase64);
    }
  }

  private async fetchNewsBanner(newsBannerId: number): Promise<void> {
    return new Promise<void>(resolve => {
      this.isLoading = true;
      this.newsBannerService
        .getNewsBannerById(newsBannerId)
        .pipe(take(1))
        .subscribe(newsBanner => {
          this.patchNewsBannerFormValues(newsBanner);
          resolve();
        });
    });
  }

  private patchNewsBannerFormValues(newsBanner: NewsBannerModel) {
    this.newsBannerForm.patchValue({
      title: newsBanner.title,
      subtitle: newsBanner.subtitle,
      description: newsBanner.description,
      teaserText: newsBanner.teaserText,
      contentType: newsBanner.contentType,
      categoryId: newsBanner.category?.id,
      link: newsBanner.link,
      linkText: newsBanner.linkText,
    });

    if (newsBanner.image) {
      this.loadImage(newsBanner.image);
    }

    this.updateValidatorsBasedOnContentType();
    this.initialFormValues = this.newsBannerForm.value;
  }

  private loadImage(image: File) {
    const reader = new FileReader();
    reader.onload = () => {
      this.selectedImage = reader.result as string;
      this.newsBannerForm.get('image')?.setValue(image);
      this.initialFormValues = this.newsBannerForm.value;
    };
    reader.readAsDataURL(image);
  }

  private updateValidatorsBasedOnContentType() {
    const contentType = this.newsBannerForm.get('contentType')?.value;

    if (contentType !== NewsBannerContentType.Image) {
      this.newsBannerForm.get('image')?.clearValidators();
    }

    if (contentType === NewsBannerContentType.Category) {
      this.newsBannerForm
        .get('categoryId')
        ?.setValidators([Validators.required]);
    }

    this.updateValidators();
  }

  private async fetchCategories(): Promise<void> {
    return new Promise<void>(resolve => {
      this.courseTypeService
        .getAllTrainingCategories(true)
        .pipe(take(1))
        .subscribe({
          next: seminarCategories => {
            this.availableCategories = seminarCategories;
            this.categoryOptions = seminarCategories.map(seminarCategory => ({
              value: seminarCategory.id,
              label: seminarCategory.name,
            }));
            this.updateSelectedCategory();
            resolve();
          },
          error: () => {
            // handle error if needed
          },
        });
    });
  }

  private updateSelectedCategory() {
    if (this.newsBannerForm.get('categoryId')?.value) {
      this.selectedCategory = this.availableCategories.find(
        category => category.id === this.newsBannerForm.get('categoryId')?.value
      )!;
    }
  }

  private handleContentTypeChange() {
    this.newsBannerForm
      .get('contentType')
      ?.valueChanges.pipe(takeUntil(this.$destroy))
      .subscribe(contentType => {
        this.resetValidators();

        switch (contentType) {
          case NewsBannerContentType.Image:
            this.setImageValidators();
            this.updateValidators();
            break;
          case NewsBannerContentType.Category:
            this.setCategoryValidators();
            this.updateValidators();
            break;
          case NewsBannerContentType.Default:
            this.selectedCategory = this.defaultCategory;
            break;
        }

        this.updateValidators();
      });
  }

  private resetValidators() {
    this.newsBannerForm.get('categoryId')?.setValue(null);
    this.newsBannerForm.get('image')?.setValue(null);
    this.selectedImage = null;
    this.selectedCategory = null;
    this.newsBannerForm.get('categoryId')?.clearValidators();
    this.newsBannerForm.get('image')?.clearValidators();
  }

  private setImageValidators() {
    this.newsBannerForm.get('image')?.setValidators([Validators.required]);
  }

  private setCategoryValidators() {
    this.newsBannerForm.get('categoryId')?.setValidators([Validators.required]);
  }

  private updateValidators() {
    this.newsBannerForm.get('image')?.updateValueAndValidity();
    this.newsBannerForm.get('categoryId')?.updateValueAndValidity();
  }

  private handleCategoryChange(): void {
    this.newsBannerForm
      .get('categoryId')
      ?.valueChanges.pipe(takeUntil(this.$destroy))
      .subscribe(categoryId => {
        if (!categoryId) return;

        this.selectedCategory = this.availableCategories.find(
          category => category.id === categoryId
        )!;
      });
  }

  onSubmit(): void {
    if (this.newsBannerForm.invalid) {
      this.formService.markFormGroupTouched(this.newsBannerForm);
      return;
    }

    this.isLoading = true;
    this.editMode ? this.updateNewsBanner() : this.createNewsBanner();
  }

  onCancel(): void {
    this.location.back();
  }

  private createNewsBanner(): void {
    const newsBannerCreateModel: NewsBannerCreateModel = {
      title: this.newsBannerForm.get('title')?.value ?? null,
      subtitle: this.newsBannerForm.get('subtitle')?.value ?? null,
      description: this.newsBannerForm.get('description')?.value ?? null,
      teaserText: this.newsBannerForm.get('teaserText')?.value ?? null,
      contentType: this.newsBannerForm.get('contentType')?.value ?? null,
      image: this.newsBannerForm.get('image')?.value ?? null,
      categoryId: this.newsBannerForm.get('categoryId')?.value ?? null,
      link: this.newsBannerForm.get('link')?.value ?? null,
      linkText: this.newsBannerForm.get('linkText')?.value ?? null,
    };

    this.newsBannerService
      .createNewsBanner(newsBannerCreateModel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert(
            'News Banner erfolgreich erstellt'
          );
          this.initialFormValues = this.newsBannerForm.value;
          this.location.back();
        },
        error: error => {
          this.alertService.showErrorAlert(
            'Fehler beim Erstellen des News Banners'
          );
          console.error('error', error);
          this.isLoading = false;
        },
      });
  }

  private updateNewsBanner(): void {
    const newsBannerUpdateModel: NewsBannerUpdateModel = {
      id: this.newsBannerId,
      title: this.newsBannerForm.get('title')?.value ?? null,
      subtitle: this.newsBannerForm.get('subtitle')?.value ?? null,
      description: this.newsBannerForm.get('description')?.value ?? null,
      teaserText: this.newsBannerForm.get('teaserText')?.value ?? null,
      contentType: this.newsBannerForm.get('contentType')?.value ?? null,
      image: this.newsBannerForm.get('image')?.value ?? null,
      categoryId: this.newsBannerForm.get('categoryId')?.value ?? null,
      link: this.newsBannerForm.get('link')?.value ?? null,
      linkText: this.newsBannerForm.get('linkText')?.value ?? null,
    };

    this.newsBannerService
      .updateNewsBanner(newsBannerUpdateModel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert(
            'News Banner erfolgreich aktualisiert'
          );
          this.initialFormValues = this.newsBannerForm.value;
          this.location.back();
        },
        error: error => {
          this.alertService.showErrorAlert(
            'Fehler beim Aktualisieren des News Banners'
          );
          this.isLoading = false;
        },
      });
  }

  onDeleteNewsBanner(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'News Banner löschen',
        message:
          'Bist du sicher, dass du diesen News Banner löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.newsBannerService
          .deleteNewsBanner(this.newsBannerId)
          .pipe(take(1))
          .subscribe(() => {
            this.alertService.showSuccessAlert(
              'News Banner erfolgreich gelöscht'
            );
            this.initialFormValues = this.newsBannerForm.value;
            this.location.back();
          });
      }
    });
  }

  get showCategoryDropdown(): boolean {
    return (
      this.newsBannerForm.get('contentType')?.value ===
      NewsBannerContentType.Category
    );
  }

  get showImageUpload(): boolean {
    return (
      this.newsBannerForm.get('contentType')?.value ===
      NewsBannerContentType.Image
    );
  }

  get showCategoryCard(): boolean {
    return (
      this.newsBannerForm.get('contentType')?.value ===
        NewsBannerContentType.Category ||
      this.newsBannerForm.get('contentType')?.value ===
        NewsBannerContentType.Default
    );
  }

  get showPlaceholderCard(): boolean {
    return (
      !this.isLoading &&
      !this.selectedCategory &&
      !this.selectedImage &&
      this.newsBannerForm.get('contentType')?.value !==
        NewsBannerContentType.Default
    );
  }

  handleImageUpload() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate the file type
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/webp',
      ];
      if (!allowedTypes.includes(file.type)) {
        this.alertService.showErrorAlert(
          'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPG, PNG oder WEBP hoch.'
        );
        // remove the file from the input
        input.files = null;
        return;
      }

      this.openImageCropperDialog(event);
    }
  }

  private openImageCropperDialog(event: Event): void {
    const dialogRef = this.dialog.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '50vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        imageChangedEvent: event,
        aspectRatio: 16 / 4,
        roundCropper: false,
        height: 316,
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((croppedImage: string) => {
      if (croppedImage) {
        this.selectedImage = croppedImage;
        const croppedImageFile = base64ToFile(croppedImage, 'cropped-image');
        this.newsBannerForm.get('image')?.setValue(croppedImageFile);
      }
    });
  }

  canDeactivate(): CanDeactivateType {
    if (this.isLoading) {
      return true;
    }

    return this.formService.confirmDeactivation(
      this.newsBannerForm.value,
      this.initialFormValues
    );
  }

  ngOnDestroy() {
    this.$destroy.next();
    this.$destroy.complete();
  }
}
